import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_AGENDA,
  MOCK_AGENDA_PAGING,
  mockAxiosResponse,
} from './tickets-mock-data';

const { get, post, patch } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/`;

///* tanStack query

export type GetAgendaParams = {
  page?: number;
  page_size?: number;
  estado?: string;
  tecnico_id?: string;
  ticket_id?: string;
  enabled?: boolean;
};

export type GetAgendaCalendarioParams = {
  desde: string;
  hasta: string;
  tecnico_id?: string;
  enabled?: boolean;
  /** Si se pasa, refresca la agenda en tiempo real cada N ms (solo la usa la Agenda). */
  refetchInterval?: number | false;
};

export type AplazarAgendaParams = {
  id: string;
  data: Record<string, unknown>;
};

export type IniciarAgendaParams = {
  id: string;
  data: Record<string, unknown>;
};

export type CompletarAgendaParams = {
  id: string;
  data: Record<string, unknown>;
};

export const useFetchAgenda = ({
  page = 1,
  page_size = 10,
  enabled = true,
  ...params
}: GetAgendaParams) => {
  return useQuery({
    queryKey: ['gestion-tickets-agenda', page, page_size, params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_AGENDA_PAGING);
      return getAgenda({ page, page_size, ...params });
    },
    enabled,
  });
};

export const useFetchAgendaCalendario = ({
  desde,
  hasta,
  tecnico_id,
  enabled = true,
  refetchInterval,
}: GetAgendaCalendarioParams) => {
  return useQuery({
    queryKey: ['gestion-tickets-agenda', 'calendario', desde, hasta, tecnico_id],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_AGENDA);
      return getAgendaCalendario({ desde, hasta, tecnico_id });
    },
    enabled,
    refetchInterval: refetchInterval ?? false,
    refetchIntervalInBackground: false,
  });
};

export const useCreateAgenda = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createAgenda,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestion-tickets-agenda'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Agenda creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useAplazarAgenda = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: aplazarAgenda,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestion-tickets-agenda'] });

      setAlert &&
        setAlert(
          true,
          'Agenda aplazada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      handleAxiosError(error);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useIniciarAgenda = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: iniciarAgenda,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestion-tickets-agenda'] });

      setAlert &&
        setAlert(
          true,
          'Agenda iniciada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      handleAxiosError(error);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useCompletarAgenda = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: completarAgenda,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gestion-tickets-agenda'] });

      setAlert &&
        setAlert(
          true,
          'Agenda completada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      handleAxiosError(error);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export const getAgenda = async (params?: GetAgendaParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get(
    `${BASE_URL}agenda/?${queryParams}`,
    true
  );
};

export const getAgendaCalendario = async (
  params: Omit<GetAgendaCalendarioParams, 'enabled'>
) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get(
    `${BASE_URL}agenda/calendario/?${queryParams}`,
    true
  );
};

export const createAgenda = async (data: Record<string, unknown>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post(
    `${BASE_URL}agenda/`,
    data,
    true
  );
};

export const aplazarAgenda = async ({
  id,
  data,
}: AplazarAgendaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return patch(
    `${BASE_URL}agenda/${id}/aplazar/`,
    data,
    true
  );
};

export const iniciarAgenda = async ({
  id,
  data,
}: IniciarAgendaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return patch(
    `${BASE_URL}agenda/${id}/iniciar/`,
    data,
    true
  );
};

export const completarAgenda = async ({
  id,
  data,
}: CompletarAgendaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return patch(
    `${BASE_URL}agenda/${id}/completar/`,
    data,
    true
  );
};
